import {type ReactElement, useCallback, useEffect, useRef, useState} from 'react';
import {PillButton} from '@Components/pill-button';
import {PillButtonSize} from '@/components/pill-button/pill-button.types';
import {IconSize} from '@/components/icon-v2/icon.types';
import styles from './ai-image-prompt-box.module.scss';
import {AiImageAspectRatioOptions, AiImageFilterOptions, AiImageModelOption, AiImageStylesOptions, MediaTypePrompt} from '@Panels/ai-image-panel/ai-image-panel.types';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';
import {PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxContextualMenu} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-contextual-menu';
import {useAiPromptBox} from '@Components/ai-prompt-box/hooks/useAiPromptBox';
import {
  type AITextToImageBackendData,
  GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE,
  type GenerateAIImageResponse,
  generateImage,
  type GenerateImageParams,
  getRemainingAiUsages,
} from '@Libraries/pmw-stock-media-library';
import {getContextualMenuGroups, getCreditCostForModel, getErrorMessage, getPlaceHolderText, getPromptBoxOptions} from '@Panels/ai-image-panel/ai-image-panel-library';
import {isUserPremium} from '@Libraries/user.library';
import {showTextToImageUpsellDialog} from '@Libraries/text-to-image-library';
import type {BuyCreditsResponse} from '@/types/credits.types';
import {noop} from '@Utils/general.util';
import {GA4EventName, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {ErrorType} from '@Components/ai-prompt-box/components/ai-prompt-box-issue/ai-prompt-box-issue.types';
import {toInteger} from 'lodash';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {updateSelectedAiImageModelId, updateSelectedAiImageModelIdBackend} from '@/reducers/user-preferences.reducer';
import type {FooterLeftAction} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {capitalize} from '@Utils/string.util';
import {AI_IMAGE_PROMPT_BOX_ID} from './ai-image-prompt-box.types';

interface AiImagePromptBoxProps {
  onImageGenerationStarted: (generatingImagesAspectRatio: AiImageAspectRatioOptions) => void;
  onImageGenerationSuccessful: (images: AITextToImageBackendData[]) => void;
  onImageGenerationFailed: () => void;
  mediaType?: MediaTypePrompt;
}

export default function AiImagePromptBox({...props}: AiImagePromptBoxProps): ReactElement<HTMLDivElement> {
  const [mediaTypePrompt, setMediaTypePrompt] = useState<MediaTypePrompt>(props.mediaType ?? MediaTypePrompt.PHOTO);
  const {actions, state, selectedContextualMenuItems} = useAiPromptBox(
    AI_IMAGE_PROMPT_BOX_ID,
    getPromptBoxOptions(mediaTypePrompt, AiImageModelOption.Auto, AiImageStylesOptions.Photographic, AiImageAspectRatioOptions.Square)
  );
  const selectedModel = toInteger(selectedContextualMenuItems[AiImageFilterOptions.AiModel][0]) as AiImageModelOption;
  const selectedStyle = selectedContextualMenuItems[AiImageFilterOptions.Styles][0] as AiImageStylesOptions;
  const selectedAspectRatio = selectedContextualMenuItems[AiImageFilterOptions.AspectRatio][0] as AiImageAspectRatioOptions;
  const dispatch = useAppDispatch();
  const selectedAiImageModelId = useAppSelector((state) => state.userPreferences.selectedAiImageModelId);
  const isExternalActionForModel = useRef(false);

  useEffect(() => {
    getRemainingTextToImageInvocations();
  }, []);

  useEffect(() => {
    actions.update({
      promptBoxState: PromptBoxState.ACTIVE,
    });
  }, []);

  useEffect(() => {
    if (props.mediaType) {
      setMediaTypePrompt(props.mediaType);
    }
  }, [props.mediaType]);

  useEffect(() => {
    onSelectedModelUpdate();
  }, [selectedModel]);

  useEffect(() => {
    const pillVisibility = [PromptBoxState.ACTIVE, PromptBoxState.LOADING].includes(state.promptBoxState);
    if (pillVisibility !== state.creditsPill?.visible) {
      actions.update({
        creditsPill: {
          visible: pillVisibility,
          actionCost: state.creditsPill?.actionCost,
          showPremiumIcon: !isUserPremium(),
          remainingCredits: state.creditsPill?.remainingCredits,
        },
      });
    }
  }, [state.promptBoxState]);

  const getRemainingTextToImageInvocations = (): void => {
    getRemainingAiUsages()
      .then((remainingCredits) => {
        actions.update({
          creditsPill: {
            remainingCredits: remainingCredits,
          },
        });
      })
      .catch((e: unknown) => {
        console.error(e);
      });
  };

  const updateSelectedMediaType = (mediaType: MediaTypePrompt): void => {
    setMediaTypePrompt(mediaType);
    const style = mediaType === MediaTypePrompt.STICKER ? AiImageStylesOptions.DigitalArt : AiImageStylesOptions.Photographic;
    if (mediaType !== MediaTypePrompt.PHOTO) {
      if (selectedModel !== AiImageModelOption.Gemini2_5) {
        isExternalActionForModel.current = true;
      }
      actions.update({
        contextualMenu: getContextualMenuGroups(AiImageModelOption.Gemini2_5, style, AiImageAspectRatioOptions.Square, mediaType),
      });
    } else if (mediaType === MediaTypePrompt.PHOTO && mediaTypePrompt !== MediaTypePrompt.PHOTO) {
      actions.update({
        contextualMenu: getContextualMenuGroups(selectedAiImageModelId ?? selectedModel, style, selectedAspectRatio, mediaType),
      });
    } else if (style !== selectedStyle) {
      actions.update({
        contextualMenu: getContextualMenuGroups(selectedModel, style, selectedAspectRatio, mediaType),
      });
    }
    actions.update({
      placeholder: getPlaceHolderText(mediaType),
    });
    updateActionCost(getCreditCostForImageGeneration(mediaType));
  };

  const handleInsufficientCreditsError = (): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.stripeCheckout.showStripeCheckoutForCreditPackPurchase(
        (data: BuyCreditsResponse) => {
          if (data.credits > 0) {
            void onGenerate();
          }
        },
        noop,
        window.i18next.t('pmwjs_purchase_more_credits_continue_using_ai_images_bg_remover_and_more'),
        window.i18next.t('pmwjs_youre_out_of_credits')
      );
    });
  };

  const getCreditCostForImageGeneration = (mediaPrompt: MediaTypePrompt): number => {
    if (mediaPrompt !== MediaTypePrompt.PHOTO) {
      return getCreditCostForModel(AiImageModelOption.Gemini2_5) + 1;
    }
    return getCreditCostForModel(selectedModel);
  };

  const updateUserModelPreference = (model: AiImageModelOption): void => {
    dispatch(updateSelectedAiImageModelId(model));
    void updateSelectedAiImageModelIdBackend(model);
  };

  const onSelectedModelUpdate = (): void => {
    updateActionCost(getCreditCostForImageGeneration(mediaTypePrompt));
    if (!isExternalActionForModel.current) {
      updateUserModelPreference(selectedModel);
    }
    isExternalActionForModel.current = false;
  };

  const updateActionCost = (actionCost: number): void => {
    if (actionCost !== state.creditsPill?.actionCost) {
      actions.update({
        creditsPill: {
          visible: state.creditsPill?.visible ?? false,
          actionCost: actionCost,
          showPremiumIcon: !isUserPremium(),
          remainingCredits: state.creditsPill?.remainingCredits ?? 0,
        },
      });
    }
  };

  const getContextualMenu: FooterLeftAction = useCallback((props): ReactElement => {
    return <AiPromptBoxContextualMenu {...props} />;
  }, []);

  const onGenerateImageSuccess = (generateImagesResponse: GenerateAIImageResponse): void => {
    props.onImageGenerationSuccessful(generateImagesResponse.generatedImages);
    actions.update({
      creditsPill: {
        visible: state.creditsPill?.visible ?? false,
        actionCost: getCreditCostForImageGeneration(mediaTypePrompt) ?? 0,
        showPremiumIcon: !isUserPremium(),
        remainingCredits: generateImagesResponse.remainingCredits,
      },
    });
  };

  const onGenerate = async (): Promise<void> => {
    if (!isUserPremium()) {
      void showTextToImageUpsellDialog();
      return;
    }

    if (!state.prompt || state.prompt.trim().length === 0) {
      return;
    }
    props.onImageGenerationStarted(selectedAspectRatio);

    actions.update({
      error: null,
      promptBoxState: PromptBoxState.LOADING,
    });

    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_GENERATE, {
      [GA4EventParamName.TYPE]: selectedStyle,
    });

    const generateImageParams: GenerateImageParams = {
      prompt: state.prompt,
      style: selectedStyle,
      aspectRatio: selectedAspectRatio,
      category: mediaTypePrompt,
      providerId: selectedModel === AiImageModelOption.Auto ? undefined : selectedModel,
    };
    try {
      setTimeout(() => {
        actions.update({
          promptBoxState: PromptBoxState.ACTIVE,
        });
      }, 1000);
      const generateImageResponse = await generateImage(generateImageParams);
      onGenerateImageSuccess(generateImageResponse);
    } catch (e) {
      if (e instanceof Error && e.message.includes(GENERATE_IMAGE_INSUFFICIENT_CREDITS_RESPONSE_CODE)) {
        handleInsufficientCreditsError();
        const errorMessage = getErrorMessage(e.message);
        if (errorMessage) {
          actions.update({
            error: {
              type: ErrorType.ERROR,
              message: errorMessage,
              icon: 'icon-warning',
            },
          });
        }
      } else {
        actions.update({
          error: {
            type: ErrorType.ERROR,
            message: window.i18next.t('pmwjs_unexpected_error_occurred'),
            icon: 'icon-warning',
          },
        });
        console.error(e);
      }
      props.onImageGenerationFailed();
    }
  };

  const getAiPromptBox = (): ReactElement<HTMLDivElement> => {
    return (
      <AiPromptBox
        containerClasses={styles.promptBoxContainer}
        id={AI_IMAGE_PROMPT_BOX_ID}
        data-testid={PosterEditorTestId.AI_IMAGE_PROMPT_BOX}
        inputTestId={PosterEditorTestId.AI_IMAGE_PROMPT_INPUT}
        submitTestId={PosterEditorTestId.AI_IMAGE_PROMPT_SUBMIT}
        initialConfig={getPromptBoxOptions(mediaTypePrompt, selectedModel, selectedStyle, selectedAspectRatio)}
        onGenerate={onGenerate}
        onRegenerate={onGenerate}
        onStop={(): Promise<void> => Promise.resolve()}
        options={[getContextualMenu]}
      />
    );
  };

  const getMediaTypePill = (type: MediaTypePrompt, text: string, icon: string): ReactElement => {
    return (
      <PillButton
        isSelected={mediaTypePrompt === type}
        size={PillButtonSize.SMALL}
        leftIconProps={{
          icon,
          size: IconSize.SIZE_ICON_16,
        }}
        text={text}
        onClick={(_) => {
          updateSelectedMediaType(type);
        }}
      />
    );
  };

  const getAdditionalPromptRow = (): ReactElement<HTMLDivElement> => {
    return (
      <div className={`flex-row-align-center spacing-p-t-3 ${styles.promptPills}`}>
        {getMediaTypePill(MediaTypePrompt.PHOTO, window.i18next.t('pmwjs_photo'), 'icon-image')}
        {getMediaTypePill(MediaTypePrompt.LOGO, capitalize(window.i18next.t('pmwjs_logo')), 'icon-logo')}
        {getMediaTypePill(MediaTypePrompt.TEXT_ART, window.i18next.t('pmwjs_text_art'), 'icon-text-art')}
        {getMediaTypePill(MediaTypePrompt.STICKER, window.i18next.t('pmwjs_sticker'), 'icon-sticker')}
      </div>
    );
  };

  return (
    <div className={'_position-relative'}>
      {getAiPromptBox()}
      {getAdditionalPromptRow()}
    </div>
  );
}
